// src/listData.js — createListDataHelper(payload) → a plural accessor over the
// ListVisualizationPayloadDto contract (contractVersion 2, kind:"list"). Each submission is
// wrapped in the EXISTING single-submission helper (createDataHelper) — exactly the pattern
// data.js repeated() already uses for child records — so authored list views compose the same
// 16 components per-row, and cross-submission accessors are thin, null-safe reductions over them.

import { createDataHelper } from './data.js';

export function createListDataHelper(payload, assets) {
  const safe = payload && typeof payload === 'object' ? payload : {};
  const assetMap = assets && typeof assets === 'object' ? assets : {};
  const rawSubs = Array.isArray(safe.submissions) ? safe.submissions : [];

  // Wrap each submission in the SAME helper a single-submission view receives.
  const items = rawSubs.map((p) => createDataHelper(p, assetMap));

  // Shared schema + form name from the TOP-LEVEL payload, so they resolve even with zero submissions;
  // fall back to the first item.
  const schemaWrapper = safe.schema && typeof safe.schema === 'object' ? safe.schema : {};
  const schemaObj = items.length && items[0].schema ? items[0].schema : parseSchema(schemaWrapper.schemaJson);
  const formName = schemaWrapper.name || (items.length ? items[0].formName : null) || 'Submissions';

  // Memoize column reductions — an authored view may call column()/aggregate() many times per render.
  const colCache = new Map();
  const numCache = new Map();

  // A column ref may be prefixed 'meta.' or 'workOrder.' to read submission meta / work-order props
  // per row (e.g. 'meta.status', 'workOrder.priority'); unprefixed refs resolve as form fields.
  // Returns undefined for an unprefixed ref so callers keep their existing it.value/text/num paths.
  function prefixedValue(it, ref) {
    if (ref.startsWith('meta.')) {
      const v = it.meta ? it.meta[ref.slice(5)] : null;
      return v === undefined ? null : v;
    }
    if (ref.startsWith('workOrder.')) {
      const v = it.workOrder ? it.workOrder[ref.slice(10)] : null;
      return v === undefined ? null : v;
    }
    return undefined;
  }

  function column(keyOrLabel) {
    const k = String(keyOrLabel == null ? '' : keyOrLabel);
    if (colCache.has(k)) return colCache.get(k);
    const out = items.map((it) => {
      const pv = prefixedValue(it, k);
      return pv === undefined ? it.value(keyOrLabel) : pv;
    });
    colCache.set(k, out);
    return out;
  }

  function numericColumn(keyOrLabel) {
    const k = String(keyOrLabel == null ? '' : keyOrLabel);
    if (numCache.has(k)) return numCache.get(k);
    const out = [];
    for (const it of items) {
      const pv = prefixedValue(it, k);
      const n = pv === undefined ? it.num(keyOrLabel) : Number(pv);
      if (n !== null && Number.isFinite(n)) out.push(n);
    }
    numCache.set(k, out);
    return out;
  }

  function textColumn(keyOrLabel) {
    const k = String(keyOrLabel == null ? '' : keyOrLabel);
    return items.map((it) => {
      const pv = prefixedValue(it, k);
      if (pv === undefined) return it.text(keyOrLabel);
      return pv == null ? '' : String(pv);
    });
  }

  // aggregate(key, fn) — fn is a reducer (acc,n,i)=>acc, or one of avg|mean|sum|min|max|count.
  // 'count' = number of non-null numeric values; avg/sum/min/max return null on an empty column.
  function aggregate(keyOrLabel, fn) {
    const nums = numericColumn(keyOrLabel);
    if (typeof fn === 'function') return nums.reduce((acc, n, i) => fn(acc, n, i), undefined);
    const op = String(fn || 'avg').toLowerCase();
    if (op === 'count') return nums.length;
    if (nums.length === 0) return null;
    if (op === 'sum') return nums.reduce((a, b) => a + b, 0);
    if (op === 'min') return Math.min(...nums);
    if (op === 'max') return Math.max(...nums);
    return nums.reduce((a, b) => a + b, 0) / nums.length; // avg / mean
  }

  // All submissions' lab results flattened, each tagged with its submission index + id for provenance.
  function labResultsAll() {
    const out = [];
    items.forEach((it, i) => {
      const rows = it.labResults();
      if (Array.isArray(rows)) {
        for (const r of rows) out.push({ ...r, submissionIndex: i, submissionId: it.meta && it.meta.id });
      }
    });
    return out;
  }

  // Coordinates per submission (for map clustering / multi-point plotting), index + label carried for pins.
  function geoAll() {
    const out = [];
    items.forEach((it, i) => {
      const g = it.geo();
      if (g) {
        out.push({
          lat: g.lat,
          lng: g.lng,
          index: i,
          label: (it.meta && (it.meta.submitterName || it.meta.locationName)) || ('#' + (i + 1)),
          status: it.meta && it.meta.status
        });
      }
    });
    return out;
  }

  // Uploaded image assets: name → data-URI (server-inlined). '' when missing so an <img src> degrades cleanly.
  // Lives at the TOP level of the list helper (unlike per-submission attachments) so a logo/header image can be
  // placed once for the whole list — e.g. html`<img src=${d.asset('logo.png')} />` in the Hero.
  function asset(name) {
    return (name && Object.prototype.hasOwnProperty.call(assetMap, name)) ? assetMap[name] : '';
  }

  // Field keys of the FIRST submission whose values parse numeric — used by autoListView for auto KPIs/columns.
  function detectNumericKeys(limit = 6) {
    if (!items.length) return [];
    const keys = [];
    for (const f of items[0].fields()) {
      if (!f || typeof f.fieldKey !== 'string') continue;
      if (isAttachOrRepeated(f)) continue;
      const v = f.value;
      if (Array.isArray(v) || v === null || v === undefined || v === '' || typeof v === 'boolean') continue;
      if (Number.isFinite(Number(v))) keys.push(f.fieldKey);
      if (keys.length >= limit) break;
    }
    return keys;
  }

  return {
    raw: safe,
    schema: schemaObj,
    formName,
    count: () => items.length,
    submissions: () => items,
    filterMeta: () => safe.filter || safe.filterMeta || null,
    column,
    numericColumn,
    textColumn,
    aggregate,
    labResultsAll,
    geoAll,
    detectNumericKeys,
    asset
  };
}

// ---- module-local helpers (mirror data.js / autoView.js) ------------------
function parseSchema(schemaJson) {
  if (!schemaJson || typeof schemaJson !== 'string') return null;
  try {
    return JSON.parse(schemaJson);
  } catch {
    return null;
  }
}

const ATTACH_REP = [
  'image', 'drawing', 'voice', 'video', 'document', 'file', 'signature',
  'repeatedform', 'repeated', 'attachment'
];
function isAttachOrRepeated(f) {
  const t = String(f.fieldType || '').toLowerCase();
  if (ATTACH_REP.includes(t)) return true;
  const v = f.value;
  return Array.isArray(v) && v.length > 0 && typeof v[0] === 'object';
}

export default createListDataHelper;
